<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'dialogUsers',
})
</script>
<template>
  <ant-Modal v-model:open="visible" transition-name="" :centered="true" :wrap-class-name="'wrap-dialog-users'"
    :title="state.dialog.title" :width="state.dialog.width" :confirm-loading="loading" :ok-text="state.dialog.submitTxt"
    @ok="onConfirm" @cancel="onClose" :closable="false" :maskClosable="false">
    <div class="form-content">
      <a-form :model="searchParam" :wrapper-col="{ style: 'flex: 1' }">
        <a-row :gutter="24">
          <a-col :xs="24" :sm="24" :md="12" :lg="12" :xl="6" class="mb-[10px]">
            <a-form-item label="用户ID:">
              <a-input v-model:value="searchParam.userNumber" placeholder="请输入用户ID" :maxlength="20" allow-clear />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="24" :md="12" :lg="12" :xl="6" class="mb-[10px]">
            <a-form-item label="手机号:">
              <a-input v-model:value="searchParam.tel" placeholder="请输入手机号" :maxlength="11" allow-clear />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="24" :md="12" :lg="12" :xl="6" class="mb-[10px]">
            <div class="card-col-action flex flex-nowrap">
              <ant-button class="search mr-[10px]!" @click="onSearch" type="primary">
                <SearchOutlined />查询
              </ant-button>
              <ant-button class="resetFun mr-[10px]!" @click="onReset" type="primary">
                <RedoOutlined />重置
              </ant-button>
            </div>
          </a-col>
        </a-row>
      </a-form>
      <a-table :columns="columns" :data-source="userList" :row-selection="rowSelection" :loading="loading" size="small"
        row-key="id" :pagination="false">
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'status'">
            <a-tag v-if="excludeIds.includes(String(record.id))" color="green">已选择</a-tag>
          </template>
        </template>
      </a-table>
      <ant-pagination v-model:current="pageNum" v-model:page-size="pageSize" :total="total"
        :page-size-options="['10', '20']" show-size-changer :show-total="(total: number) => `共 ${total} 条`"
        :showQuickJumper="true" @change="onPageChange" @show-size-change="onShowSizeChange" />
    </div>
  </ant-Modal>
</template>

<script setup lang="ts" name="dialogUsers">
import { reactive, ref, computed } from 'vue'
import { SearchOutlined, RedoOutlined } from '@ant-design/icons-vue'
import { useUser } from '@/store/user'
import { fetchWithPaginationFallback } from '@/utils/paginationFallback'

interface SelectedUser {
  id: string
  username: string
  tel: string
}

const emit = defineEmits(['confirm'])

const userStore = useUser()

const state = reactive({
  dialog: {
    visible: false,
    title: '选择用户',
    submitTxt: '确认',
    width: '969px'
  }
})

const visible = computed({
  get: () => state.dialog.visible,
  set: (v) => {
    state.dialog.visible = v
  }
})
const loading = ref(false)
const userList = ref<any[]>([])
const tempSelectedKeys = ref<string[]>([])
const excludeIds = ref<string[]>([])
const pageNum = ref(1)
const pageSize = ref(10)
const total = ref(0)

const searchParam = reactive({
  userNumber: '',
  tel: ''
})

const columns = [
  { title: '用户昵称', dataIndex: 'username', key: 'username' },
  { title: '手机号', dataIndex: 'tel', key: 'tel' },
  { title: '状态', key: 'status', width: 100 }
]

const rowSelection = computed(() => ({
  type: 'checkbox' as const,
  selectedRowKeys: tempSelectedKeys.value,
  onChange: (selectedRowKeys: any[]) => {
    tempSelectedKeys.value = selectedRowKeys.map(String)
  },
  getCheckboxProps: (record: any) => {
    const isSelected = excludeIds.value.includes(String(record.id))
    return { disabled: isSelected }
  }
}))

const fetchList = async (page = 1, size = 10) => {
  loading.value = true
  try {
    const params: any = { pageNum: page, pageSize: size }
    if (searchParam.userNumber) params.userNumber = searchParam.userNumber
    if (searchParam.tel) params.tel = searchParam.tel
    const data: any = await fetchWithPaginationFallback(
      (param) => userStore.getUserPage(param),
      params
    )
    userList.value = (data.records || []).map((item: any) => ({ ...item, id: String(item.id) }))
    total.value = data.total || 0
    pageNum.value = data.pageNum ?? pageNum.value
  } catch {
    userList.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

const onSearch = () => {
  pageNum.value = 1
  fetchList(1, pageSize.value)
}

const onReset = () => {
  searchParam.userNumber = ''
  searchParam.tel = ''
  pageNum.value = 1
  fetchList(1, pageSize.value)
}

const onPageChange = (page: number) => {
  pageNum.value = page
  fetchList(page, pageSize.value)
}

const onShowSizeChange = (_current: number, size: number) => {
  pageSize.value = size
  pageNum.value = 1
  fetchList(1, size)
}

const onConfirm = () => {
  const selected = userList.value.filter((item: any) => tempSelectedKeys.value.includes(String(item.id))).map((item: any) => ({ id: String(item.id), username: item.username, tel: item.tel }))
  emit('confirm', selected)
  onClose()
}

const onClose = () => {
  visible.value = false
  tempSelectedKeys.value = []
  pageNum.value = 1
  total.value = 0
  searchParam.userNumber = ''
  searchParam.tel = ''
}

const openDialog = (exclude: string[]) => {
  excludeIds.value = exclude
  tempSelectedKeys.value = []
  pageNum.value = 1
  visible.value = true
  fetchList(1, pageSize.value)
}

defineExpose({ openDialog })
</script>

<style scoped lang="less">
.form-content {
  padding: 30px 30px 22px 30px;
}

:deep(.ant-form-item) {
  margin-bottom: 0px;
  padding-bottom: 24px;
}

:deep(.ant-form-item-label) {
  margin-left: 0px;
  margin-right: 5px;
  width: 80px;
  text-align: right;
  justify-content: flex-end;
  display: flex;
  align-items: center;
  height: 40px;
}

:deep(.ant-form-item-label > label) {
  font-size: 14px;
  line-height: 1;
  font-weight: 400;
}

:deep(.ant-input-affix-wrapper) {
  height: 40px;
  padding: 0 11px;
  border-radius: 4px;
  display: flex;
  align-items: center;
}

:deep(.ant-input-disabled) {
  height: 40px;
  border-color: #C5C5D3;
  border-radius: 4px;
}

:deep(.ant-select-selector) {
  height: 40px !important;
  display: flex;
  align-items: center;
  border-radius: 4px;
}

// 只覆盖默认态，hover/focus 保留 Ant Design 蓝色
:deep(.ant-input-affix-wrapper:not(:hover):not(.ant-input-affix-wrapper-focused)) {
  border-color: #C5C5D3;
}

// 只覆盖默认态，hover/focus 保留 Ant Design 蓝色
:deep(.ant-select:not(.ant-select-disabled):not(.ant-select-focused) .ant-select-selector:not(:hover)) {
  border-color: #C5C5D3;
}

// 只覆盖默认态，hover/focus 保留 Ant Design 蓝色
:deep(textarea.ant-input:not(:hover):not(:focus)) {
  border-color: #C5C5D3;
}

.card-col-action {
  display: flex;
  align-items: center;
  height: 40px;
}
</style>
